import { lazy, memo, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import config from 'config';

import { authSelector } from 'store/selectors';
import { useLatest, useRouter, useStore, useToast } from 'hooks';

export const routes = [
  {
    path: '/',
    exact: true,
    enabled: true,
    component: lazy(() => import('pages/index'))
  },
  {
    path: '/login',
    exact: false,
    enabled: true,
    component: lazy(() => import('pages/login'))
  },
  {
    path: '/components',
    exact: false,
    enabled: ['local', 'development'].includes(config.app.env),
    component: lazy(() => import('pages/components'))
  },
  {
    path: '/pricing',
    exact: false,
    enabled: true,
    component: lazy(() => import('pages/pricing'))
  },
  {
    path: '/payment',
    exact: true,
    enabled: true,
    isProtected: true,
    redirectTo: '/',
    component: lazy(() => import('pages/payment'))
  },
  {
    path: '/profile',
    exact: true,
    enabled: true,
    isProtected: true,
    redirectTo: '/',
    component: lazy(() => import('pages/profile'))
  },
  {
    path: '/stats/:eventCode',
    exact: false,
    enabled: true,
    isProtected: true,
    redirectTo: '/',
    component: lazy(() => import('pages/stats/[eventCode]'))
  },
  {
    path: '/get-paid',
    exact: true,
    enabled: true,
    isProtected: true,
    redirectTo: '/',
    component: lazy(() => import('pages/get-paid'))
  },
  {
    path: `/uzbekittv`,
    exact: true,
    enabled: true,
    component: lazy(() => import('pages/uzbekittv'))
  },
  {
    path: `/uzbekittv/obuna`,
    exact: true,
    enabled: true,
    component: lazy(() => import('pages/uzbekittv/obuna'))
  },
  {
    path: '/:channelName?',
    exact: true,
    enabled: true,
    component: lazy(() => import('pages/[[...channelName]]'))
  },
  {
    path: '/uzbekittv/:eventCode/:inviteeCode?',
    exact: false,
    enabled: true,
    component: lazy(() => import('pages/uzbekittv/[eventCode]/[[...inviteeCode]]'))
  },
  {
    path: '/:channelName?/:eventCode/:inviteeCode?',
    exact: false,
    enabled: true,
    component: lazy(() => import('pages/[[...channelName]]/[eventCode]/[[...inviteeCode]]'))
  }
];

const AppRoutes = (): JSX.Element => {
  const toast = useToast();
  const router = useRouter();
  const { auth } = useStore(state => ({
    auth: authSelector(state)
  }));

  const latestToasts = useLatest(toast.toasts);

  useEffect(() => {
    const resetListener = router.history.listen(() => {
      latestToasts.current
        .filter(t => t.options?.duration === Infinity || t.options?.autoDismiss === false)
        .forEach(t => {
          toast.dismiss(t.id);
        });
    });

    return () => {
      resetListener();
    };
  }, []);

  return (
    <Switch>
      {routes
        .filter(route => route.enabled)
        .map(({ path, exact, component, isProtected, redirectTo }) => {
          if (!auth.isAuth && isProtected && redirectTo) {
            return <Redirect key={path} from={path} to={redirectTo} />;
          }
          return <Route key={path} {...{ path, exact, component }} />;
        })}
    </Switch>
  );
};

const memoizedAppRoutes = memo(AppRoutes);

export default memoizedAppRoutes;
